import { Coins, Heart, type LucideIcon, Target, Trophy } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

/**
 * OKS-468 — W7 §04 welcome teaser. A 2×2 grid of four static, informational
 * cells previewing the crew primitives the creator just unlocked. Evolved in
 * place from the OKS-384 3-card `OnboardingGamificationCards` (sole consumer
 * was the Welcome page).
 *
 * **P0 domain rule (correctness, not style):** the two crew economies are kept
 * honest in copy — XP = status/rank progression; coins = a spendable currency
 * for the rewards catalog. The labels are verbatim per Figma §04 and MUST NOT
 * conflate the two (never "earn coins to rank up" / "XP → rewards").
 *
 * Cells are informational only — non-focusable, non-interactive. Icons are
 * decorative (`aria-hidden`); meaning is carried by the label text (a11y).
 *
 * Animation: a subtle motion-safe staggered fade-up on mount (≤480ms total).
 * Under `prefers-reduced-motion` no transform runs and every cell is fully
 * visible immediately (base opacity is 1 — the entrance only adds motion).
 */
interface TeaserCell {
  key: "xp" | "ranks" | "coins" | "recognised"
  Icon: LucideIcon
}

const TEASER_CELLS: readonly TeaserCell[] = [
  { key: "xp", Icon: Target },
  { key: "ranks", Icon: Trophy },
  { key: "coins", Icon: Coins },
  { key: "recognised", Icon: Heart },
] as const

export function OnboardingTeaserGrid({ className }: { className?: string }) {
  const { t } = useTranslation()

  return (
    <div
      className={cn("grid w-full grid-cols-2 gap-2.5", className)}
      data-testid="wizard-welcome-teaser"
    >
      {TEASER_CELLS.map(({ key, Icon }, index) => (
        <div
          key={key}
          data-testid={`wizard-welcome-teaser-${key}`}
          className={cn(
            "flex flex-col items-center gap-2 rounded-xl border py-3 text-center",
            "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:fill-mode-both motion-safe:duration-300",
          )}
          // Staggered entrance — 4 cells × 60ms keeps the total under 480ms.
          style={{ animationDelay: `${index * 60}ms` }}
        >
          <span className="bg-primary/10 rounded-xl p-2.5">
            <Icon className="text-primary size-5" aria-hidden="true" />
          </span>
          <span className="text-xs font-medium">
            {t(`wizard.welcome.teaser.${key}`)}
          </span>
        </div>
      ))}
    </div>
  )
}

export default OnboardingTeaserGrid
